<template>
  <div>
    <gg-card>
      <gg-card-header>
        DNS Editor
      </gg-card-header>
      <gg-card-text>
        <v-container class="grid-size-xs pa-0" fluid>
          <v-row>
            <v-col cols="12" class="d-block">
              <gg-btn color="primary" class="mr-2" tile :disabled="loading" @click="openActionModal('add')">
                Add new record
              </gg-btn>
              <gg-btn :disabled="loading" tile class="mx-2" @click="setRecordsToModel">
                Refresh records
              </gg-btn>
            </v-col>

            <!-- Add new record -->
            <records-edit v-if="isAddModeActive"
                            :is-active="isAddModeActive"
                            edit-mode="add"
                            title="Add record"
                            :hint="dnsHint"
                            :allowed_types="allowed_types"
                            :record="edited_record"
                            :loading="loading"
                            :submit-function="addNewRecord"
                            @changed="listenRecord"
                            @close="closeDialog"
              />

            <!-- Edit existing record -->
            <records-edit v-if="isEditModeActive"
                          :is-active="isEditModeActive"
                          edit-mode="edit"
                          title="Edit record"
                          :hint="dnsHint"
                          :submit-function="editExistingRecord"
                          :allowed_types="allowed_types"
                          :record="edited_record"
                          :loading="loading"
                          @changed="listenRecord"
                          @close="closeDialog"
              />

            <!-- Delete record -->
            <records-edit v-if="isDeleteModeActive"
                          :is-active="isDeleteModeActive"
                          edit-mode="delete"
                          title="Are you sure want to delete this record?"
                          :hint="dnsHint"
                          :submit-function="deleteRecord"
                          :allowed_types="allowed_types"
                          :record="edited_record"
                          :loading="loading"
                          @changed="listenRecord"
                          @close="closeDialog"
              />

            <!-- DataTable -->
            <v-col cols="12">
              <gg-loading :loading="loading" type="table">
                <v-data-table :headers="headers"
                              :items="records"
                              :footer-props="{ itemsPerPageOptions: pagination }"
                >
                  <template v-slot:item.actions="{ item, index }">
                    <gg-btn tile
                            class="mr-1"
                            color="primary"
                            :disabled="loading"
                            @click="openActionModal('edit', item, index)"
                    >
                      Edit
                    </gg-btn>
                    <gg-btn tile
                            color="gray"
                            :disabled="loading"
                            @click="openActionModal('delete', item, index)">
                      Delete
                    </gg-btn>
                  </template>
                </v-data-table>
              </gg-loading>
            </v-col>
          </v-row>
        </v-container>
      </gg-card-text>
    </gg-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { deepCopy } from 'Helpers/helpers'
import RecordsEdit from 'Components/Domains/DnsEditor/RecordsEdit'

export default {
  name: 'NameServersEditor',
  components: {
    RecordsEdit
  },
  props: {
    domainid: {
      type: String,
      required: true,
    },
    domain: {
      type: String,
      required: true
    }
  },
  data: () => ({
    headers: [
      {
        text: 'Type',
        value: 'type'
      },
      {
        text: 'Name',
        value: 'name'
      },
      {
        text: 'Address',
        value: 'address'
      },
      {
        text: 'Actions',
        align: 'right',
        value: 'actions'
      }
    ],
    records: [],
    allowed_types: [],
    edited_record: {
      type: '',
      name: '',
      mxpref: '',
      address: ''
    },
    record_index: null,
    loading: false,
    dialog: '',
  }),
  async created () {
    await this.setRecordsToModel()
  },
  computed: {
    ...mapGetters(['pagination']),
    isAddModeActive () {
      return this.dialog === 'add'
    },
    isEditModeActive () {
      return this.dialog === 'edit'
    },
    isDeleteModeActive () {
      return this.dialog === 'delete'
    },
    dnsHint () {
      let enteredName = this.edited_record.name === '' ? '[name]' : this.edited_record.name
      let enteredAddress = this.edited_record.address === '' ? '[address]' : this.edited_record.address

      return `${enteredName}.${this.domain} will resolve to ${enteredAddress}`
    },
  },
  provide () {
    // Validation
    return { parentValidator: this.$validator }
  },
  methods: {
    ...mapActions([
      'getDnsRecords',
      'setDnsRecords',
      'reportSuccess',
      'reportError'
    ]),
    async setDns (records) {
      try {
        const data = {
          domain_id: this.domainid,
          records: records,
        }
        return await this.setDnsRecords(data)
      } catch (e) {
        throw e
      }
    },
    async setRecordsToModel () {
      try {
        this.loading = true

        const response = await this.getDnsRecords({ domainid: this.domainid })

        this.records = response.records
        this.allowed_types = response.allowed_types
        this.loading = false
      } catch (e) {
        this.reportError(e)
        this.loading = false
      }
    },
    async addNewRecord () {
      try {
        if (!await this.$validator.validateAll()) return
        this.loading = true

        let records = deepCopy(this.records)
        records.push(this.edited_record)

        const response = await this.setDnsRecords({
          domain_id: this.domainid,
          records,
        })

        this.reportSuccess(response.resultmsg)
        this.closeDialog()
        await this.setRecordsToModel()
        this.loading = false
      } catch (e) {
        this.reportError(e)
        this.loading = false
      }
    },
    async editExistingRecord () {
      if (!await this.$validator.validateAll()) return
      this.loading = true

      try {
        let current_records = deepCopy(this.records)
        let edited_record = deepCopy(this.edited_record)
        let edited_record_index = this.record_index

        current_records.splice(edited_record_index, 1, edited_record)

        const response = await this.setDnsRecords({
          domain_id: this.domainid,
          records: current_records,
        })
        this.closeDialog()
        await this.setRecordsToModel()
        this.reportSuccess(response.resultmsg)
      } catch (e) {
        this.reportError(e)
      } finally {
        this.loading = false
      }
    },
    async deleteRecord () {
      try {
        this.loading = true

        let current_records = deepCopy(this.records)
        let deleted_record_index = this.record_index

        current_records.splice(deleted_record_index, 1)

        const response = await this.setDnsRecords({
          domain_id: this.domainid,
          records: current_records
        })

        this.closeDialog()
        await this.setRecordsToModel()
        this.reportSuccess(response.resultmsg)
        this.loading = false
      } catch (e) {
        this.reportError(e)
        this.loading = false
      }
    },
    openActionModal (type = '', record = {}, i = null) {
      this.dialog = type

      if (type !== 'add') {
        this.edited_record = Object.assign({}, record)
        this.record_index = i
      }
    },
    closeDialog () {
      this.dialog = ''

      this.clearTmpRecord()
    },
    clearTmpRecord () {
      this.edited_record.type = ''
      this.edited_record.name = ''
      this.edited_record.address = ''
      this.edited_record.mxpref = ''
      this.record_index = null
    },
    listenRecord (record) {
      this.edited_record.type = record.type
      this.edited_record.name = record.name
      this.edited_record.address = record.address
      if (record.mxpref) {
        this.edited_record.mxpref = record.mxpref
      }
    }
  },
}
</script>
